import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Parse from "parse";
import CLKKWidget from "./CLKKWidget.jsx";
import ClkkDialog from "./ClkkDialog.jsx";
import { Box, Button, TextField, Typography, Alert } from "@mui/material";

Parse.initialize(
  process.env.REACT_APP_APP_ID,
  null,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

const RechargeWidgetPage = () => {
  const [searchParams] = useSearchParams();

  const platform = searchParams.get("platform") || "desktop";
  const type = searchParams.get("type"); // 'recharge' or 'redeem'
  const sessionToken = searchParams.get("sessionToken");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // user details
  const [userId, setUserId] = useState(null);
  const [walletId, setWalletId] = useState(null);
  const [remark, setRemark] = useState(null);
  const [balance, setBalance] = useState(0);

  // amount flow
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [showWidget, setShowWidget] = useState(false);

  const [rechargeAllowed, setRechargeAllowed] = useState(false);
  const [redeemAllowed, setRedeemAllowed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sessionToken) {
        setError("Missing sessionToken in the URL.");
        setLoading(false);
        return;
      }

      try {
        // ✅ Look up the session directly
        const Session = Parse.Object.extend("_Session");
        const query = new Parse.Query(Session);
        query.equalTo("sessionToken", sessionToken);
        query.include("user");
        const sessionObj = await query.first({ useMasterKey: true });

        if (!sessionObj) throw new Error("Invalid or expired sessionToken.");
        const user = sessionObj.get("user");

        if (!cancelled && user) {
          setUserId(user.id);
          setWalletId(user.get("walletId") || null);
          setRemark(user.get("remark") || null);
          setBalance(user.get("balance") || 0);

          // 👇 NEW: Fetch settings & validate
          const [rechargeSetting, cashoutSetting] = await Promise.all([
            new Parse.Query("Settings")
              .equalTo("type", "rechargeEnabled")
              .first({ useMasterKey: true }),
            new Parse.Query("Settings")
              .equalTo("type", "cashoutEnabled")
              .first({ useMasterKey: true }),
          ]);

          const rechargeGlobal =
            rechargeSetting?.get("settings")?.[0] === "true";
          const cashoutGlobal = cashoutSetting?.get("settings")?.[0] === "true";

          let rechargeAllowedTmp = rechargeGlobal;
          let redeemAllowedTmp = cashoutGlobal;

          if (!rechargeGlobal) {
            const allowedRechargeList = await new Parse.Query("Settings")
              .equalTo("type", "allowedAgentsForRecharge")
              .first({ useMasterKey: true });
            const allowedRechargeIds =
              allowedRechargeList?.get("settings") || [];

            const manager = user.get("manager");
            const managerId = manager?.id

            console.log(managerId,"managerIdmanagerIdmanagerIdmanagerId")
            if (managerId && allowedRechargeIds.includes(managerId)) {
              rechargeAllowedTmp = true;
            }
          }

          if (!cashoutGlobal) {
            const allowedCashoutList = await new Parse.Query("Settings")
              .equalTo("type", "allowedAgentsForCashout")
              .first({ useMasterKey: true });
            const allowedCashoutIds = allowedCashoutList?.get("settings") || [];

            const manager = user.get("manager");
            const managerId = manager?.id
            console.log(managerId,"managerIdmanagerIdmanagerIdmanagerId")
            if (managerId && allowedCashoutIds.includes(managerId)) {
              redeemAllowedTmp = true;
            }
          }

          setRechargeAllowed(rechargeAllowedTmp);
          setRedeemAllowed(redeemAllowedTmp);
          setLoading(false);
        }
      } catch (err) {
        console.error("Session validation error:", err);
        if (!cancelled) {
          setError("Invalid User Access");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionToken]);

  const handleProceed = () => {
    const num = parseFloat(amount);
    if (isNaN(num)) {
      setAmountError("Please enter a valid amount");
      return;
    }
    if (num < 25) {
      setAmountError("Minimum redeem/recharge amount is $25");
      return;
    }
    if (num > 500) {
      setAmountError("Maximum redeem/recharge amount is $500");
      return;
    }
    if (type === "redeem" && num > balance) {
      setAmountError("Amount cannot exceed your balance");
      return;
    }

    setAmountError("");
    setShowWidget(true);
  };

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          bgcolor: "black",
          color: "white",
        }}
      >
        Loading&hellip;
      </Box>
    );
  if (error)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          bgcolor: "black",
          color: "red",
        }}
      >
        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </Box>
    );

  // 👇 If REDEEM, show balance, amount input, and ClkkDialog
  if (type === "redeem") {
    if (!showWidget) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            gap: 2,
            bgcolor: "black",
            color: "white",
          }}
        >
          <Typography variant="h6">Redeem Balance</Typography>
          <Typography variant="body1">
            Available Balance: {Number(balance ?? 0).toFixed(2)}
          </Typography>
          <Box sx={{ width: "100%", maxWidth: 200, mx: "auto" }}>
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 25, max: 500 }}
              fullWidth
            />
          </Box>
          {amountError && <Alert severity="error">{amountError}</Alert>}
          <Button
            variant="contained"
            onClick={handleProceed}
            style={{
              background: "#FFC107",
              color: "#0A0A0A",
              textTransform: "none",
              "&:hover": { backgroundColor: "#FFA000" },
            }}
          >
            Proceed
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, bgcolor: "black", color: "white", minHeight: "100vh" }}>
        <ClkkDialog
          open
          onClose={() => setShowWidget(false)}
          amount={amount}
          method={null}
          availableMethods={["paypal", "venmo", "card"]}
          handleRefresh={() => console.log("refresh after payout")}
          userId={userId}
          sessionToken={sessionToken}
        />
      </Box>
    );
  }
  // 👇 If REDEEM
  if (type === "redeem") {
    if (!redeemAllowed) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            gap: 2,
            bgcolor: "black",
            color: "red",
          }}
        >
          <Alert severity="error">Redeem is disabled for your manager</Alert>
        </Box>
      );
    }
  }

  // 👇 If RECHARGE
  if (type === "recharge") {
    if (!rechargeAllowed) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            gap: 2,
            bgcolor: "black",
            color: "red",
          }}
        >
          <Alert severity="error">Recharge is disabled for your manager</Alert>
        </Box>
      );
    }
  }

  // 👇 If RECHARGE, normal flow
  if (!showWidget) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          bgcolor: "black",
          color: "white",
        }}
      >
        <Typography variant="h6">Enter Recharge Amount</Typography>
        <TextField
          type="number"
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 1 }}
        />
        {amountError && <Alert severity="error">{amountError}</Alert>}
        <Button
          variant="contained"
          onClick={handleProceed}
          style={{
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": { backgroundColor: "#FFA000" },
          }}
        >
          Proceed
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
        bgcolor: "black",
        color: "white",
      }}
    >
      <CLKKWidget
        open={showWidget}
        onClose={() => setShowWidget(false)}
        walletId={walletId}
        remark={remark}
        platform={platform}
        type={type}
        userId={userId}
        amount={parseFloat(amount)}
        sessionToken={sessionToken}
        onOptionClick={(id, details) => {
          console.log("Clicked:", id, details);
        }}
      />
    </Box>
  );
};

export default RechargeWidgetPage;
